import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { API_URL } from './utils/authUtils';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = API_URL;

// Helper function to construct full S3 URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If already a full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Construct S3 URL for scholars-uploads bucket
  const S3_BASE_URL = 'https://scholars-uploads.s3.us-east-2.amazonaws.com';
  return `${S3_BASE_URL}/${imagePath}`;
};

// Custom marker icons for different categories
// Priority: applied (green) > interested (blue) > matched (purple)
const getMarkerIcon = (categories) => {
  const colors = {
    interested: '#3B82F6',  // Blue
    applied: '#10B981',     // Green
    matched: '#8B5CF6'      // Purple
  };

  // Determine primary color based on priority
  let primaryColor = colors.matched; // default
  if (categories?.includes('applied')) {
    primaryColor = colors.applied;
  } else if (categories?.includes('interested')) {
    primaryColor = colors.interested;
  }

  return L.divIcon({
    html: `<div style="background-color: ${primaryColor};
                      width: 20px; height: 20px;
                      border-radius: 50%; border: 2px solid white;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [20, 20],
    className: 'custom-marker'
  });
};

// Student popup component
const StudentMapPopup = ({ student, onStudentAction, holdFeatureEnabled, shortlistedIds }) => {
  const isHeld = student.hold_status?.is_on_hold;
  const canTakeActions = !isHeld || student.hold_status?.held_by_current_recruiter;
  const isShortlisted = shortlistedIds.includes(student.id);

  return (
    <div className="w-64 p-2">
      {/* Student Header */}
      <div className="flex items-center mb-3">
        {student.headshot ? (
          <img 
            src={getImageUrl(student.headshot)}
            alt={`${student.first_name} ${student.last_name}`}
            className="w-12 h-12 rounded-full mr-3"
          />
        ) : (
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-3">
            {student.first_name?.[0]}{student.last_name?.[0]}
          </div>
        )}
        <div>
          <h4 className="font-medium text-gray-900">
            {student.first_name} {student.last_name}
          </h4>
          <div className="flex flex-wrap gap-2 mt-1">
            {student.categories?.includes('interested') && (
              <span className="flex items-center gap-1 text-xs">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-blue-600">Favorited You</span>
              </span>
            )}
            {student.categories?.includes('applied') && (
              <span className="flex items-center gap-1 text-xs">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-green-600">Applied</span>
              </span>
            )}
            {student.categories?.includes('matched') && (
              <span className="flex items-center gap-1 text-xs">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span className="text-purple-600">Matched</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Student Details */}
      <div className="space-y-2 text-sm mb-3">
        <div><strong>University:</strong> {student.current_university_verified}</div>
        <div><strong>Major:</strong> {student.major_verified}</div>
        <div><strong>GPA:</strong> {student.gpa_verified || 'N/A'}</div>
        <div><strong>Year:</strong> {student.current_academic_class_verified}</div>
        {(student.city || student.state) && (
          <div><strong>Location:</strong> {[student.city, student.state, student.zip].filter(Boolean).join(', ')}</div>
        )}
        
        {/* Status Badges */}
        <div className="flex space-x-1 mt-2">
          <span className={`px-2 py-1 text-xs rounded ${
            student.partner_has_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {student.partner_has_verified ? 'Verified' : 'Pending'}
          </span>
          <span className={`px-2 py-1 text-xs rounded ${
            student.eligible_sp25 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {student.eligible_sp25 ? 'Eligible' : 'Pending'}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {student.resume && (
          <button
            onClick={() => window.open(student.resume, '_blank')}
            className="w-full px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            View Resume
          </button>
        )}
        
        {canTakeActions && onStudentAction && (
          <>
            <button
              onClick={() => onStudentAction('shortlist', student.id)}
              className={`w-full px-3 py-1 text-sm rounded ${
                isShortlisted 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
            >
              {isShortlisted ? 'Added to Shortlist' : 'Add to Shortlist'}
            </button>
            
            {holdFeatureEnabled && !isHeld && (
              <button
                onClick={() => onStudentAction('hold', student.id)}
                className="w-full px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Place Hold
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const RecruiterMapView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recruiterId, setRecruiterId] = useState(null);
  const [recruiterInfo, setRecruiterInfo] = useState(null);
  
  // Student data
  const [interestedStudents, setInterestedStudents] = useState([]);
  const [appliedStudents, setAppliedStudents] = useState([]);
  const [matchedStudents, setMatchedStudents] = useState([]);
  const [shortlistedIds, setShortlistedIds] = useState([]);
  
  // Map states
  const [holdFeatureEnabled, setHoldFeatureEnabled] = useState(false);
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]); // Geographic center of US
  const [mapZoom, setMapZoom] = useState(4);

  // Filter states
  const [filters, setFilters] = useState({
    categories: ['interested', 'applied', 'matched'],
    showOnlyWithCoordinates: true
  });

  useEffect(() => {
    // Get recruiter ID from localStorage
    const recruiterInfo = localStorage.getItem('recruiterInfo');
    
    if (!recruiterInfo) {
      // No recruiter info, redirect to login
      navigate('/recruiter/login');
      return;
    }

    try {
      const info = JSON.parse(recruiterInfo);
      setRecruiterId(info.id);
      setRecruiterInfo(info);
    } catch (e) {
      console.error('Error parsing recruiter info:', e);
      navigate('/recruiter/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (recruiterId) {
      fetchDashboardData();
      checkHoldFeature();
    }
  }, [recruiterId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('recruiterToken');
      
      if (!token) {
        navigate('/recruiter/login');
        return;
      }

      // Use per_page=10000 to load ALL students for map display (bypasses pagination)
      const response = await axios.get(
        `${BACKEND_URL}/api/recruiter/dashboard/${recruiterId}?per_page=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = response.data;
      setInterestedStudents(data.interested_students || []);
      setAppliedStudents(data.applied_students || []);
      setMatchedStudents(data.matched_students || []);
      setShortlistedIds(data.shortlisted_ids || []);
      setRecruiterInfo(data.recruiter_info);
      
      setError('');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        navigate('/recruiter/login');
      } else {
        setError('Failed to load student data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkHoldFeature = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/hold_feature_enabled/public`);
      setHoldFeatureEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error checking hold feature:', error);
      setHoldFeatureEnabled(false);
    }
  };

  const handleStudentAction = async (action, studentId) => {
    if (action === 'shortlist') {
      try {
        const token = localStorage.getItem('recruiterToken');
        if (!token) {
          navigate('/recruiter/login');
          return;
        }

        const response = await axios.post(
          `${BACKEND_URL}/api/recruiter/shortlist/${studentId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status === 200) {
          // Update the shortlisted IDs list
          const isCurrentlyShortlisted = shortlistedIds.includes(studentId);
          if (isCurrentlyShortlisted) {
            // Remove from shortlist
            setShortlistedIds(prev => prev.filter(id => id !== studentId));
          } else {
            // Add to shortlist
            setShortlistedIds(prev => [...prev, studentId]);
          }
        }
      } catch (error) {
        console.error('Error updating shortlist:', error);
        setError('Failed to update shortlist. Please try again.');
      }
    } else {
      // Placeholder for other actions (hold, etc.)
      console.log(`Action: ${action}, Student ID: ${studentId}`);
      // TODO: Implement other actions (hold, etc.)
    }
  };

  // Combine all students - deduplicated by ID, tracking multiple categories
  const studentMap = new Map();

  // Add interested students
  interestedStudents.forEach(s => {
    if (!studentMap.has(s.id)) {
      studentMap.set(s.id, {...s, categories: ['interested']});
    } else {
      const existing = studentMap.get(s.id);
      if (!existing.categories.includes('interested')) {
        existing.categories.push('interested');
      }
    }
  });

  // Add applied students
  appliedStudents.forEach(s => {
    if (!studentMap.has(s.id)) {
      studentMap.set(s.id, {...s, categories: ['applied']});
    } else {
      const existing = studentMap.get(s.id);
      if (!existing.categories.includes('applied')) {
        existing.categories.push('applied');
      }
    }
  });

  // Add matched students
  matchedStudents.forEach(s => {
    if (!studentMap.has(s.id)) {
      studentMap.set(s.id, {...s, categories: ['matched']});
    } else {
      const existing = studentMap.get(s.id);
      if (!existing.categories.includes('matched')) {
        existing.categories.push('matched');
      }
    }
  });

  const allStudents = Array.from(studentMap.values());

  // Filter students based on current filters
  const filteredStudents = allStudents.filter(student => {
    // Category filter - show if student has ANY of the selected categories
    const hasSelectedCategory = student.categories.some(cat => filters.categories.includes(cat));
    if (!hasSelectedCategory) {
      return false;
    }

    // Coordinate filter
    if (filters.showOnlyWithCoordinates && (!student.latitude || !student.longitude)) {
      return false;
    }

    return true;
  });

  const mappableStudents = filteredStudents.filter(
    student => student.latitude && student.longitude
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading map data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                🗺️ Geographic Student Distribution
              </h1>
              <p className="text-gray-600 mt-2">
                Interactive map showing locations of students interested in {recruiterInfo?.organization || 'your company'}
              </p>
            </div>
            <button
              onClick={() => navigate('/recruiter/dashboard')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{interestedStudents.length}</div>
              <div className="text-sm text-gray-600">Favorited You</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{appliedStudents.length}</div>
              <div className="text-sm text-gray-600">Applied</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{matchedStudents.length}</div>
              <div className="text-sm text-gray-600">Matched</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">{mappableStudents.length}</div>
              <div className="text-sm text-gray-600">On Map</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Map Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-96 sm:h-[600px]">
            {mappableStudents.length > 0 ? (
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {mappableStudents.map(student => (
                  <Marker
                    key={student.id}
                    position={[student.latitude, student.longitude]}
                    icon={getMarkerIcon(student.categories)}
                  >
                    <Popup>
                      <StudentMapPopup 
                        student={student}
                        holdFeatureEnabled={holdFeatureEnabled}
                        shortlistedIds={shortlistedIds}
                        onStudentAction={handleStudentAction}
                      />
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <div className="text-4xl mb-4">🗺️</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No students to map</h3>
                  <p className="text-gray-600">
                    No students have location data available for mapping.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Students need to provide their address information for geographic visualization.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        {mappableStudents.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Map Legend</h3>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-sm text-gray-600">Applied ({appliedStudents.filter(s => s.latitude && s.longitude).length} of {appliedStudents.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-sm text-gray-600">Favorited You ({interestedStudents.filter(s => s.latitude && s.longitude).length} of {interestedStudents.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded-full border-2 border-white shadow"></div>
                <span className="text-sm text-gray-600">Matched ({matchedStudents.filter(s => s.latitude && s.longitude).length} of {matchedStudents.length})</span>
              </div>
            </div>

            {/* Category Guide */}
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                How do these categories work?
              </summary>
              <div className="mt-3 space-y-3 text-gray-600 bg-gray-50 rounded-lg p-4">
                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Category Definitions</h4>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><span className="text-green-600 font-medium">Applied</span> — Students who submitted an application for the current recruitment cycle</li>
                    <li><span className="text-blue-600 font-medium">Favorited You</span> — Students who marked your company as a preferred employer</li>
                    <li><span className="text-purple-600 font-medium">Matched</span> — Students who match your criteria but have not yet applied or favorited you</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Overlapping Categories</h4>
                  <p className="ml-2">Students can be in <strong>both</strong> "Applied" and "Favorited You" categories simultaneously. When this happens:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                    <li>The marker shows as <span className="text-green-600 font-medium">green</span> (Applied takes priority)</li>
                    <li>Click the marker to see all applicable category badges</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-1">About "Matched" Students</h4>
                  <p className="ml-2">"Matched" is an <strong>exclusive</strong> category — it only includes students who match your recruiting criteria but have <em>neither</em> applied <em>nor</em> favorited your company. Once a student takes either action, they move out of "Matched" and into the appropriate category.</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-1">Map Counts</h4>
                  <p className="ml-2">The "(X of Y)" shows how many students have geocoded addresses. Students without valid addresses won't appear on the map but are still in your dashboard.</p>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecruiterMapView;